import React from "react";
import DashboardKpiStatus from "components/DashboardKpiStatus";
import { overviewChartPalette } from "styles/theme";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
} from "@mui/material";
import { CiCalendar } from "react-icons/ci";
import { FaRupeeSign } from "react-icons/fa";
import { useGetContractManagementImportantKPIQuery } from "store/slices/apis/contractManagementDashboardSlice";
import { useParams } from "react-router-dom";

const formatDate = (date) => {
  if (!date) return "-";
  const d = new Date(date);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "0.00 Cr";

  return `${Number(value).toFixed(2)} Cr`;
};

const ContractKpi = (props) => {

  const { RaisedType } = props

  const { projectId } = useParams();
  const params = {
    projectId: projectId,
    raisedType: RaisedType
  }
  const { data, isLoading, isFetching } = useGetContractManagementImportantKPIQuery(params)

  const kpiData = data?.data || {};

  const kpis = [
    {
      key: "contract-start-date",
      label: "Contractual Start Date",
      value: kpiData?.contractualStartDate,
      bg: "#DCFCE7",
      iconBg: overviewChartPalette.teal,
      type: "date",
    },
    {
      key: "contract-completion-date",
      label: "Original Contractual Completion Date",
      value: kpiData?.originalContractualCompletionDate,
      bg: "#DCFCE7",
      iconBg: overviewChartPalette.teal,
      type: "date",
    },
    {
      key: "original-contract-value",
      label: "Original Contract Value",
      value: formatCurrency(kpiData?.originalContractValue),
      bg: "#FFE2E5",
      iconBg: overviewChartPalette.coral,
      type: "rupee",
    },
    {
      key: "revised-contract-value",
      label: "Revised Contract Value",
      value: formatCurrency(kpiData?.revisedContractValue),
      bg: "#FFF4DE",
      iconBg: overviewChartPalette.amber,
      type: "rupee",
    },
    {
      key: "claims",
      label: "Approved Claims",
      value: formatCurrency(kpiData?.approvedClaims),
      bg: "#FFF4DE",
      iconBg: overviewChartPalette.cyan,
      type: "rupee",
    },
    {
      key: "change-orders",
      label: "Approved Change Order",
      value: formatCurrency(kpiData?.approvedChangeOrder),
      bg: "#FFE2E5",
      iconBg: overviewChartPalette.coral,
      type: "rupee",
    },
    {
      key: "eot",
      label: "Extension of Time",
      value: kpiData?.eotClaimedDays ?? 0,
      bg: "#FFE2E5",
      iconBg: overviewChartPalette.violet,
      //type: "rupee",
      subTexts: [
        { label: "EOT", value: kpiData?.eotClaimedDays ?? 0, },
        { label: "Approved Days", value: kpiData?.approvedDays ?? 0, },
      ],
    },
  ];

  return (
    <Box
      elevation={0}
      sx={{
        borderRadius: "3px",
        border: "1px solid #eef0f4",
        backgroundColor: "#FFFFFF",
        width: "100%",
        boxSizing: "border-box",
        p: "20px 20px 20px 20px",
        maxHeight: { xs: "auto", md: "316px" },
      }}
    >
      <Typography
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontWeight: 600,
          fontSize: "15px",
          lineHeight: "22px",
          letterSpacing: "0px",
          color: "#05004E",
        }}
      >
        Commercial Intelligence
      </Typography>
      <Typography
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontWeight: 400,
          fontSize: "13px",
          lineHeight: "20px",
          letterSpacing: "0px",
          color: "#737791",
          mb: 2,
        }}
      >
        Key Contract KPIs Overview for proactive management.
      </Typography>

      <Grid
        container
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          gap: 1.25,
          width: "100%",
          "& > .MuiGrid-root": { minWidth: 0, width: "auto", maxWidth: "none" },
          "& .MuiCard-root": { transition: "border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease" },
          "& .MuiCard-root:hover": { borderColor: "#CBD5E1", boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)", transform: "translateY(-1px)" },
        }}
      >
        {kpis?.map((item) => (
          <Grid
            xs={12}
            sm={6}
            md={12 / 7}
            key={item.label}
            sx={{ minWidth: 0 }}
          >
            <Card
              elevation={0}
              sx={{
                height: "128px",
                  position: "relative",
                // width: { minWidth: "200px", minWidth: "100%" },

                borderRadius: "3px",
                backgroundColor: "#FFFFFF",
                  border: "1px solid #E2E8F0",
                  borderBottom: `3px solid ${item.iconBg}`,
              }}
            >
              <CardContent sx={{ gap: 1, p: 1.25, pt: 1.25 }}>
                <Avatar
                  sx={{
                    width: 30,
                    height: 30,
                    backgroundColor: item.iconBg,
                    fontSize: 18,
                    fontWeight: 700,
                  }}
                >
                  {item.type === "rupee" ? <FaRupeeSign /> : <CiCalendar />}
                </Avatar>
                  <DashboardKpiStatus color={item.iconBg} />

                <>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "13px",
                      mt: "4px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Outfit, sans-serif",
                        fontWeight: 600,
                        fontSize: "16px",
                        lineHeight: "21px",
                        color: "#151D48",
                      }}
                    >
                      {item.type === "date"
                        ? formatDate(item.value)
                        : item.value}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: "Outfit, sans-serif",
                      fontWeight: 500,
                      lineHeight: "16px",
                      fontSize: "12px",
                      letterSpacing: "0px",
                      color: "#425166",
                      mt: "4px",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {item.label}
                  </Typography>
                  {item?.subTexts?.length && (
                    <Typography sx={{ mt: 0.25, fontSize: "10px", lineHeight: "13px", color: "#667085", whiteSpace: "nowrap" }}>
                      {item.subTexts.map((subItem) => `${subItem.label}: ${subItem.value}`).join(" | ")}
                    </Typography>
                  )}
                </>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ContractKpi;
